import { createResource, getResource, postgRESTApiUrl } from "./api";

export const MESSAGES_PAGE_SIZE = 30;

/**
 * Fetch a page of messages for a conversation. Fetched newest-first for the keyset
 * walk, returned oldest-first for display.
 * @param {string} conversationId - The conversation UUID
 * @param {number} receiverProfileId - The receiver's numeric profile id (stable across renames; slug is not)
 * @param {object} options
 * @param {object} options.cursor - Keyset cursor `{ sentAt, messageId }` for the next older page; null = newest page
 * @param {number} options.limit - Page size (default 30)
 * @returns {Promise<{data: Array, error?: object}>}
 */
const list = async (conversationId, receiverProfileId, { cursor = null, limit = MESSAGES_PAGE_SIZE } = {}) => {
  // Keyset walks backwards in time; the message_id tiebreak keeps paging stable across sent_at ties.
  const cursorFilter = cursor
    ? [["or", `(sent_at.lt.${cursor.sentAt},and(sent_at.eq.${cursor.sentAt},message_id.lt.${cursor.messageId}))`]]
    : [];

  const params = new URLSearchParams([
    ["conversation_id", `eq.${conversationId}`],
    ["receiver_profile_id", `eq.${receiverProfileId}`],
    ["order", "sent_at.desc,message_id.desc"],
    ["limit", String(limit)],
    ...cursorFilter,
  ]);

  const url = `${postgRESTApiUrl()}/rest/v1/conversation_messages?${params}`;
  const result = await getResource(url);
  return { ...result, data: result.data ? [...result.data].reverse() : result.data };
};

// Map a client attachment to the backend-permitted keys. Two shapes:
// - file/image: { file_cloudinary: <base64>, filename } — bytes base64'd through Rails
// - video: { public_id, resource_type:"video", format, duration, bytes, filename } — a reference to
//   an asset already uploaded direct to Cloudinary (see api/videoUploads.js)
export const toAttachmentParam = (a) => {
  if (a.resource_type === "video" && a.public_id) {
    return {
      public_id: a.public_id,
      resource_type: a.resource_type,
      format: a.format,
      duration: a.duration,
      bytes: a.bytes,
      filename: a.filename,
      recorded: a.recorded,
    };
  }
  return { filename: a.filename, file_cloudinary: a.file_cloudinary };
};

/**
 * Send a reply to a conversation
 * @param {string} conversationId - The conversation UUID
 * @param {string} body - The message body (HTML)
 * @param {Array<object>} [attachments] - base64 file/image items or video reference items
 * @returns {Promise<{data: object, error?: object}>}
 */
const sendReply = async (conversationId, body, attachments = [], { videoRequest = false } = {}) => {
  const url = `/interact/api/messaging/conversations/${conversationId}/replies`;
  return createResource(url, {
    data: {
      attributes: {
        body,
        content_format: "html",
        attachments: attachments.map(toAttachmentParam),
        // Intent stamp from the "Request a video" quick reply; the server maps it into
        // message metadata so the recipient CTA survives any wording edits.
        video_request: !!videoRequest,
      },
    },
  });
};

/**
 * Sign a direct browser->Cloudinary video upload (bytes never pass through Rails).
 * @returns {Promise<{data: object, error?: object}>} signed params (signature, api_key, upload_url, …)
 */
const signVideoUpload = async () => createResource("/interact/api/messaging/video_signature", {});

export default { list, sendReply, signVideoUpload };
